import React, { useEffect, useState } from 'react'

import { enrollmentHelperClient } from '@api/enrollmentClient'
import { getXCLEARCorrelationInfo } from '@api/utils'
import LookUpForm, {
  ApplicationIdLookupFormSubmission,
  BiographicsLookupFormSubmission,
  LookupFormFields,
  LookupFormSubmission,
} from '@components/pages/shared/LookUpForm'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { useRouter } from 'next/router'
import { KPI } from '@utils/customEvents'
import PageContent from '@components/layout/PageContent'
import ApplicationFound from '@components/ApplicationFound'
import VerificationCodeForm, {
  ValidateVerificationCodeSubmitted,
} from '@components/VerificationCodeForm'
import { ApplicantStatusDetails } from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import { Modal } from '@components/shared/Modal'
import { PlanTypes, useEnrollmentContext } from '@contexts/EnrollmentContext'
import { useLookupFlow } from '@hooks/useLookupFlow'
import { useFlag } from '@hooks/useFlag'
import { handleScenario } from '@utils/resolver'
import CollectContactInfo, {
  ValidatedContactInfo,
} from '@components/SelectVerificationMethodForm'
import { PageHeading } from '@components/layout/Heading'
import { Box } from '@clear/design-system'
import { useLookupPipe } from '@hooks/useLookupPipe'
import { resolveStatusSuccessCode } from '@hooks/useLookupPipe/lib/resolveSuccessCode'

type PageState = Partial<
  LookupFormFields & {
    otpSessionId?: string
  } & ValidatedContactInfo
>

const DEFAULT_VALUES: PageState = {
  applicationId: undefined,
  countryOfBirth: undefined,
  dob: undefined,
  emailAddress: undefined,
  firstName: undefined,
  lastName: undefined,
  middleName: undefined,
  noMiddleName: undefined,
  otpSessionId: undefined,
  phoneNumber: undefined,
} as const

const PAGE_TITLE = 'Check Status'

type LookupSubmissionOptions = {
  contact?: Pick<ValidatedContactInfo, 'emailAddress' | 'phoneNumber'>
  meta: {
    kpi: KPI
    xCLEARCorrelationInfo: string
  }
}

function StatusPage() {
  const router = useRouter()
  const { setSelectedPlan } = useEnrollmentContext()
  const { getResolver } = useLookupFlow()
  const pipe = useLookupPipe()
  const flag = useFlag('updateLookupUx') ?? { enabled: undefined }

  const [responseData, setResponseData] =
    useState<ApplicantStatusDetails | null>(null)
  const [pageState, setPageState] = useState<PageState>({ ...DEFAULT_VALUES })
  const [pageVariant, setPageVariant] = useState<
    'SendOTP' | 'VerifyOTP' | 'ApplicationFound' | ''
  >('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    pipe.view()
  }, [pipe, router.isReady])

  const onBiographicsSearchSubmission = async (
    { type: _, ...data }: BiographicsLookupFormSubmission,
    { contact, meta }: LookupSubmissionOptions
  ) => {
    const info = contact ?? {}
    setPageState({ ...DEFAULT_VALUES, ...data, ...info })

    pipe.search({ status: 'begin', searchType: 'biographics' })

    try {
      const response =
        await enrollmentHelperClient.updatedAppStatusLookupBiographic({
          xCLEARCorrelationInfo: meta.xCLEARCorrelationInfo,
          biographicsLookup: {
            ...data,
            ...info,
            dob: new Date(data.dob),
          },
        })

      if (!!response?.otpSessionId && !response.needCommMethod) {
        pipe.search({
          status: 'success',
          code: 'OTP_SENT',
          hasOtpSessionId: true,
        })
        setPageState((prev) => ({
          ...prev,
          otpSessionId: response.otpSessionId,
        }))
        setPageVariant('VerifyOTP')
      }
    } catch (error: unknown) {
      handleScenario(getResolver('status', 'biographics'), error, (code) => {
        if (flag.enabled && code === 'MULTIPLE_MEMBERS') {
          pipe.search({
            status: 'success',
            code: 'DISAMBIGUATION',
            hasOtpSessionId: false,
            disambiguation: true,
          })
          setPageVariant('SendOTP')
        } else {
          pipe.search({ status: 'failure', error, code })
          setShowErrorModal(true)
        }
      })
    }
  }

  const onApplicationIdSearchSubmission = async (
    { type: _, ...data }: ApplicationIdLookupFormSubmission,
    { meta }: LookupSubmissionOptions
  ) => {
    setPageState({ ...DEFAULT_VALUES, ...data })

    pipe.search({ status: 'begin', searchType: 'applicationId' })

    try {
      const response =
        await enrollmentHelperClient.updatedAppStatusLookupByApplicationId({
          xCLEARCorrelationInfo: meta.xCLEARCorrelationInfo,
          applicationId: data.applicationId,
        })

      if (response?.otpSessionId) {
        pipe.search({
          status: 'success',
          code: 'OTP_SENT',
          hasOtpSessionId: true,
        })
        setPageState((prev) => ({
          ...prev,
          otpSessionId: response.otpSessionId,
        }))
        setPageVariant('VerifyOTP')
      }
    } catch (error: unknown) {
      handleScenario(getResolver('status', 'applicationId'), error, (code) => {
        pipe.search({ status: 'failure', error, code })
        setShowErrorModal(true)
      })
    }
  }

  async function onSendOTP(formData?: ValidatedContactInfo): Promise<void> {
    setIsLoading(true)
    const isCodeResent = formData === undefined
    const emailAddress = isCodeResent
      ? (pageState.emailAddress as string)
      : formData.emailAddress
    const phoneNumber = isCodeResent
      ? (pageState.phoneNumber as string)
      : formData.countryCode + '.' + formData.phoneNumber.replace(/\D/g, '')
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const meta = { kpi: KPI.USER_INTERACTION, xCLEARCorrelationInfo }

    pipe.disambiguation({ status: 'begin' })

    await onBiographicsSearchSubmission(
      {
        type: 'biographics',
        firstName: pageState.firstName,
        lastName: pageState.lastName,
        middleName: pageState.middleName,
        noMiddleName: pageState.noMiddleName,
        countryOfBirth: pageState.countryOfBirth,
        dob: pageState.dob,
      } as BiographicsLookupFormSubmission,
      {
        contact: { emailAddress, phoneNumber },
        meta,
      }
    )
    setIsLoading(false)
  }

  async function onBiographicsVerifyOTP(req: {
    otpSessionId: string
    xCLEARCorrelationInfo: string
    biographicsLookup: Enrollment.BiographicsLookup
  }) {
    pipe.lookup({ status: 'begin' })
    try {
      const response =
        await enrollmentHelperClient.updatedAppStatusLookupSession(req)
      if (response) {
        pipe.lookup({
          status: 'success',
          code: resolveStatusSuccessCode(response),
        })
        /** known bug about events dropped when emitted too close together */
        setTimeout(() => pipe.conclude(), 3_000)
        setResponseData(response)
        setPageVariant('ApplicationFound')
      }
    } catch (error: unknown) {
      handleScenario(
        getResolver('status', 'biographicsLookup'),
        error,
        (code) => {
          pipe.lookup({ status: 'failure', error, code })
          setShowErrorModal(true)
        }
      )
    }
  }

  async function onApplicationIdVerifyOTP(req: {
    otpSessionId: string
    applicationId: string
    xCLEARCorrelationInfo: string
  }) {
    pipe.lookup({ status: 'begin' })
    try {
      const response =
        await enrollmentHelperClient.updatedAppStatusLookupSessionByApplicationId(
          req
        )
      if (response) {
        pipe.lookup({
          status: 'success',
          code: resolveStatusSuccessCode(response),
        })
        /** known bug about events dropped when emitted too close together */
        setTimeout(() => pipe.conclude(), 3_000)
        setResponseData(response)
        setPageVariant('ApplicationFound')
      }
    } catch (error: unknown) {
      handleScenario(
        getResolver('status', 'applicationIdLookup'),
        error,
        (code) => {
          pipe.lookup({ status: 'failure', error, code })
          setShowErrorModal(true)
        }
      )
    }
  }

  async function onVerifyOTP(formData: ValidateVerificationCodeSubmitted) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const otpSessionId = pageState?.otpSessionId ?? ''

    pipe.verifyOTP({ status: 'begin' })

    await enrollmentHelperClient
      .optAppstatusChallenge({
        otpSessionId,
        otpCode: formData.verificationCode,
        xCLEARCorrelationInfo,
      })
      .then(async () => {
        pipe.verifyOTP({ status: 'success' })
        if (pageState.applicationId) {
          await onApplicationIdVerifyOTP({
            otpSessionId,
            applicationId: pageState.applicationId,
            xCLEARCorrelationInfo,
          })
        } else {
          await onBiographicsVerifyOTP({
            otpSessionId,
            xCLEARCorrelationInfo,
            biographicsLookup: {
              ...(pageState as Required<PageState>),
              ...formData,
            },
          })
        }
      })
      .catch((error: unknown) => {
        handleScenario(getResolver('status', 'verifyOTP'), error, (code) => {
          pipe.verifyOTP({ status: 'failure', error, code })
          setShowErrorModal(true)
        })
      })
    setIsLoading(false)
  }

  const onClickEnroll = () => {
    setSelectedPlan(PlanTypes.ENROLL)
    router.push('/enroll/document-check')
  }

  return (
    <>
      {pageVariant === 'VerifyOTP' && !!pageState?.otpSessionId ? (
        <PageContent>
          <PageHeading>{PAGE_TITLE}</PageHeading>
          <Box
            width={['95%', '572px']}
            marginLeft="auto"
            marginRight="auto"
            border="1px solid #CCCED1"
            borderRadius="8px"
            paddingX={['20px', '50px']}
            paddingY={['20px', '40px']}
            backgroundColor="neutralsWhite"
          >
            <VerificationCodeForm
              isLoading={isLoading}
              otpFailureMessage={undefined}
              contactInfo={{
                otpSessionId: pageState.otpSessionId,
                emailAddress: pageState.emailAddress,
                phoneNumber: pageState.phoneNumber,
              }}
              onSubmitCode={onVerifyOTP}
              onResendCode={() => onSendOTP()}
            />
          </Box>
        </PageContent>
      ) : pageVariant === 'SendOTP' ? (
        <CollectContactInfo
          onSubmit={onSendOTP}
          variant={flag.enabled ? 'lookup' : 'default'}
          pageTitle={PAGE_TITLE}
        />
      ) : pageVariant === 'ApplicationFound' && responseData ? (
        <ApplicationFound data={responseData} />
      ) : (
        <LookUpForm
          isSearchableByApplicationId={flag.enabled}
          formTitle={`${PAGE_TITLE} | TSA&nbsp;PreCheck® Enrollment Provided by CLEAR`}
          formSubtitle="Check the status of your TSA&nbsp;PreCheck® membership application."
          pageTitle={PAGE_TITLE}
          onSubmit={async (data: LookupFormSubmission) => {
            setIsLoading(true)
            const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
            const meta = { kpi: KPI.USER_INTERACTION, xCLEARCorrelationInfo }

            if (data.type === 'app') {
              await onApplicationIdSearchSubmission(data, { meta })
            } else {
              await onBiographicsSearchSubmission(data, { meta })
            }
            setIsLoading(false)
          }}
        />
      )}
      <Modal
        showModal={showErrorModal}
        setShowModal={setShowErrorModal}
        header="Information Not Found"
        body="We couldn't find a record based on the information entered. If you have been issued a KTN and are attempting to renew, please confirm you've entered the correct information and try again. You can contact customer service at CLEARCustomerSupport@tsa.dhs.gov for additional assistance or begin a new enrollment."
        successButtonText="Enroll"
        canClose={true}
        successClick={onClickEnroll}
      />
    </>
  )
}

export default StatusPage
